import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import { Box, ButtonBase, IconButton, Stack, Typography } from '@mui/material';
import { useState } from 'react';

/**
 * Every catalogue photo of one device, in the order the team set (#189). The URLs are presigned
 * by the backend; this component only shows them.
 */
export function PublicDeviceGallery({
  photos,
  deviceName,
}: {
  photos: string[];
  deviceName: string;
}) {
  const [selected, setSelected] = useState(0);
  const total = photos.length;

  if (!total) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        spacing={1}
        sx={{
          aspectRatio: '4 / 3',
          borderRadius: '16px',
          bgcolor: 'grey.100',
          color: 'text.secondary',
        }}
      >
        <PhotoCameraOutlinedIcon />
        <Typography variant="body2">Este equipo todavía no tiene fotos.</Typography>
      </Stack>
    );
  }

  const current = Math.min(selected, total - 1);
  const show = (index: number) => setSelected((index + total) % total);

  return (
    <Stack spacing={1.5}>
      <Box
        sx={{
          position: 'relative',
          aspectRatio: '4 / 3',
          borderRadius: '16px',
          overflow: 'hidden',
          bgcolor: 'grey.100',
        }}
      >
        <Box
          component="img"
          src={photos[current]}
          alt={`${deviceName}, foto ${current + 1} de ${total}`}
          sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
        />
        {total > 1 ? (
          <>
            <IconButton
              aria-label="Ver la foto anterior"
              onClick={() => show(current - 1)}
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,.9)',
                boxShadow: '0 1px 4px rgba(11,13,16,.24)',
                '&:hover': { bgcolor: 'background.paper' },
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              aria-label="Ver la foto siguiente"
              onClick={() => show(current + 1)}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,.9)',
                boxShadow: '0 1px 4px rgba(11,13,16,.24)',
                '&:hover': { bgcolor: 'background.paper' },
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </>
        ) : null}
      </Box>
      {total > 1 ? (
        <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 0.5 }}>
          {photos.map((photo, index) => (
            <ButtonBase
              key={photo}
              aria-label={`Ver la foto ${index + 1} de ${total}`}
              aria-current={index === current ? 'true' : undefined}
              onClick={() => show(index)}
              sx={{
                flex: '0 0 auto',
                width: 72,
                height: 54,
                borderRadius: '8px',
                overflow: 'hidden',
                border: 2,
                borderColor: index === current ? 'primary.main' : 'transparent',
                bgcolor: 'grey.100',
              }}
            >
              <Box
                component="img"
                src={photo}
                alt=""
                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </ButtonBase>
          ))}
        </Stack>
      ) : null}
    </Stack>
  );
}
